import { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCreateRole,
  useDeleteRole,
  usePermissions,
  useRoles,
  useUpdateRole,
  type RoleSummary,
} from '@/hooks/use-roles';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

/**
 * YUKI-392 / RL-688: catalogue keys for permission descriptions the Roles
 * screen shows translated. A permission's description is otherwise the
 * English text its module registered; one listed here renders through
 * `t(key, description)`, so a missing translation still falls back to that
 * English text.
 */
const PERMISSION_DESCRIPTION_KEY: Record<string, string> = {
  'till.cash-refund.override': 'roles.permission.tillCashRefundOverride',
  'till.session.end-locked': 'roles.permission.tillSessionEndLocked',
};

/** The description the Roles screen shows for a permission. */
function permissionDescription(
  t: (key: string, fallback?: string) => string,
  code: string,
  description: string,
): string {
  const key = PERMISSION_DESCRIPTION_KEY[code];
  return key === undefined ? description : t(key, description);
}

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

/**
 * RL-483: Back Office refuses to delete a role cashiers still hold (a cashier
 * losing its role used to become unrestricted at the till). The 409 names the
 * holders; say who they are, so the operator knows whom to reassign, instead
 * of echoing the raw error. Null for any other failure.
 */
export function roleHeldByCashiersMessage(
  err: unknown,
  t: (key: string, fallback: string) => string,
): string | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  const body = err.response.data as
    | {
        code?: string;
        cashierCount?: number;
        cashiers?: { employeeCode: string; displayName: string }[];
      }
    | undefined;
  if (body?.code !== 'ROLE_HELD_BY_CASHIERS') return null;
  const listed = body.cashiers ?? [];
  const count = body.cashierCount ?? listed.length;
  const names = listed.map((c) => `${c.displayName} (${c.employeeCode})`).join(', ');
  return t(
    'roles.editor.heldByCashiers',
    'This role is still assigned to {count} cashier(s): {names}. Give them another role on the Cashiers page first.',
  )
    .replace('{count}', String(count))
    .replace('{names}', count > listed.length ? `${names}, …` : names);
}

export function RolesPage() {
  const t = useT();
  const { data: roles } = useRoles();
  const { data: perms } = usePermissions();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Auto-select first role on load (manager).
  useEffect(() => {
    if (selected === null && roles !== undefined && roles.length > 0 && !creating) {
      setSelected(roles[0]!.id);
    }
  }, [roles, selected, creating]);

  const current = useMemo(
    () => (roles ?? []).find((r) => r.id === selected) ?? null,
    [roles, selected],
  );

  return (
    <div className="grid grid-cols-[260px_1fr] gap-5">
      <aside className="space-y-2">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">{t('roles.title', 'Roles')}</h1>
          <Button
            size="sm"
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            {t('roles.new', 'New')}
          </Button>
        </header>
        <ul className="rounded-md border border-border bg-card text-sm">
          {(roles ?? []).map((r) => (
            <li
              key={r.id}
              className={cn(
                'cursor-pointer border-b border-border px-3 py-2 last:border-b-0',
                selected === r.id && !creating
                  ? 'bg-secondary font-medium'
                  : 'hover:bg-secondary/60',
              )}
              onClick={() => {
                setSelected(r.id);
                setCreating(false);
              }}
            >
              <div className="flex items-center justify-between">
                <span>{r.name}</span>
                {/* VL-73: a store-group role is read-only like a built-in one. */}
                {(r.isBuiltIn || r.sourceDerived === true) && (
                  <Badge variant="outline" className="text-[10px]">
                    {t('roles.builtIn', 'built-in')}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {(r.userCount === 1
                  ? t('roles.list.userOne', '{count} user')
                  : t('roles.list.userMany', '{count} users')
                ).replace('{count}', String(r.userCount))}{' '}
                ·{' '}
                {(r.permissionCodes.length === 1
                  ? t('roles.list.permissionOne', '{count} permission')
                  : t('roles.list.permissionMany', '{count} permissions')
                ).replace('{count}', String(r.permissionCodes.length))}
              </p>
            </li>
          ))}
        </ul>
      </aside>

      <main>
        {creating ? (
          <RoleEditor
            initial={{
              id: '',
              name: '',
              description: '',
              isBuiltIn: false,
              userCount: 0,
              permissionCodes: [],
            }}
            permissions={perms ?? []}
            mode="create"
            onSaved={(saved) => {
              setCreating(false);
              setSelected(saved.id);
            }}
            onCancel={() => setCreating(false)}
          />
        ) : current === null ? (
          <p className="text-sm text-muted-foreground">
            {t('roles.empty', 'Pick a role on the left.')}
          </p>
        ) : (
          <RoleEditor
            initial={current}
            permissions={perms ?? []}
            mode="edit"
            onSaved={() => undefined}
            onDeleted={() => setSelected(null)}
          />
        )}
      </main>
    </div>
  );
}

function RoleEditor({
  initial,
  permissions,
  mode,
  onSaved,
  onCancel,
  onDeleted,
}: {
  initial: RoleSummary;
  permissions: { code: string; description: string | null; module: string }[];
  mode: 'create' | 'edit';
  onSaved: (r: RoleSummary) => void;
  onCancel?: () => void;
  onDeleted?: () => void;
}) {
  const t = useT();
  const create = useCreateRole();
  const update = useUpdateRole();
  const remove = useDeleteRole();
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [granted, setGranted] = useState<Set<string>>(new Set(initial.permissionCodes));
  const [banner, setBanner] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // RL-372: resync whenever a field the form renders changes upstream (a
  // rename elsewhere, a permission grant), not only when a different role
  // is picked — otherwise Save writes the stale values back. The grant set
  // is keyed by its sorted codes so a refetch returning the same role (a new
  // array) does not wipe in-progress edits.
  const initialDescription = initial.description ?? '';
  const initialGrantKey = [...initial.permissionCodes].sort().join('\n');
  useEffect(() => {
    setName(initial.name);
    setDescription(initialDescription);
    setGranted(new Set(initialGrantKey === '' ? [] : initialGrantKey.split('\n')));
    setBanner(null);
  }, [initial.id, initial.name, initialDescription, initialGrantKey]);

  const grouped = useMemo(() => {
    const out = new Map<string, typeof permissions>();
    for (const p of permissions) {
      const arr = out.get(p.module) ?? [];
      arr.push(p);
      out.set(p.module, arr);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  function toggle(code: string) {
    setGranted((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function onSave() {
    setBanner(null);
    try {
      if (mode === 'create') {
        const saved = await create.mutateAsync({
          name: name.trim(),
          description: description.trim() === '' ? null : description.trim(),
          permissionCodes: Array.from(granted),
        });
        onSaved(saved);
      } else {
        const saved = await update.mutateAsync({
          id: initial.id,
          body: {
            name: name.trim(),
            description: description.trim() === '' ? null : description.trim(),
            permissionCodes: Array.from(granted),
          },
        });
        onSaved(saved);
      }
    } catch (err) {
      setBanner(extractError(err) ?? t('roles.editor.saveFailed', 'Save failed.'));
    }
  }

  async function onDelete() {
    setBanner(null);
    try {
      await remove.mutateAsync(initial.id);
      setConfirmDelete(false);
      onDeleted?.();
    } catch (err) {
      setConfirmDelete(false);
      setBanner(
        roleHeldByCashiersMessage(err, t) ??
          extractError(err) ??
          t('roles.editor.deleteFailed', 'Delete failed.'),
      );
    }
  }

  // VL-73: a role derived from a store system's cashier group is converged
  // from the store on every import; Back Office refuses edits, so the editor
  // shows it read-only like a built-in role.
  const isBuiltIn = mode === 'edit' && (initial.isBuiltIn || initial.sourceDerived === true);

  return (
    <div className="space-y-5">
      <header>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold">
            {mode === 'create' ? t('roles.editor.newTitle', 'New role') : initial.name}
          </h2>
          {isBuiltIn && (
            <Badge variant="outline">
              {t('roles.editor.builtInBadge', 'built-in (read-only)')}
            </Badge>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4">
        <div>
          <Label htmlFor="name">{t('roles.editor.name', 'Name')}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isBuiltIn}
            className="mt-1"
          />
        </div>
        <div>
          <Label>{t('roles.editor.userCount', 'User count')}</Label>
          <p className="mt-1 text-sm">{initial.userCount}</p>
        </div>
        <div className="col-span-2">
          <Label htmlFor="description">{t('roles.editor.description', 'Description')}</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isBuiltIn}
            rows={2}
            className="mt-1"
          />
        </div>
      </div>

      <section className="space-y-3 rounded-md border border-border bg-card p-4">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-medium">{t('roles.editor.permissions', 'Permissions')}</h3>
          <p className="text-xs text-muted-foreground">
            {t('roles.editor.granted', '{granted} / {total} granted')
              .replace('{granted}', String(granted.size))
              .replace('{total}', String(permissions.length))}
          </p>
        </header>
        <div className="grid grid-cols-2 gap-4">
          {grouped.map(([module, perms]) => (
            <div key={module}>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">{module}</p>
              <ul className="space-y-1 text-xs">
                {perms.map((p) => (
                  <li key={p.code} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={granted.has(p.code)}
                      onChange={() => toggle(p.code)}
                      disabled={isBuiltIn}
                      className="mt-0.5"
                    />
                    <span className="font-mono">{p.code}</span>
                    {p.description && (
                      <span className="text-muted-foreground">
                        — {permissionDescription(t, p.code, p.description)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {banner !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {banner}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onSave} disabled={isBuiltIn || create.isPending || update.isPending}>
          {create.isPending || update.isPending
            ? t('common.saving', 'Saving…')
            : mode === 'create'
              ? t('roles.editor.create', 'Create role')
              : t('common.save', 'Save')}
        </Button>
        {mode === 'create' && onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
        )}
        {mode === 'edit' && !isBuiltIn && (
          <Button
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
            disabled={remove.isPending}
          >
            {t('roles.editor.delete', 'Delete')}
          </Button>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('roles.editor.confirmDeleteTitle', 'Delete role "{name}"?').replace(
                '{name}',
                initial.name,
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'roles.editor.confirmDeleteBody',
                'Permission grants on this role are removed. Refuses if any user is still assigned — reassign them first.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>
              {t('roles.editor.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
